import type { SupabaseClient } from '@supabase/supabase-js';
import { ucCodigoKeys } from './ucCodigo.ts';

/**
 * Checagem de UC duplicada antes de gravar.
 *
 * A mesma unidade tem dois números (o da Equatorial, '3.073.813.012-04', e o
 * código antigo, '10015106754'), guardados em `numero_uc` e `outro_numero_uc`.
 * Comparar só `numero_uc` pelo texto deixou passar a mesma UC com os números
 * invertidos ou com máscara — em set/2026 eram 102 grupos de cadastros repetidos,
 * cada um aparecendo como um cliente diferente na tela de UCs.
 *
 * A trava de verdade é a trigger `trg_bloqueia_uc_duplicada` (migration
 * 20260916150000), que vale para qualquer caminho. Isto aqui só antecipa o aviso
 * na tela, com o nome do cliente, e traduz o erro do banco quando ele chega.
 */

export interface UcComNumeros {
  id: number | string;
  numero_uc?: string | null;
  outro_numero_uc?: string | null;
}

export interface UcExistente {
  id: number;
  numero_uc: string;
  outro_numero_uc: string | null;
  nome_cliente: string | null;
  negociacao_id: number | null;
  /** Usado quando o vendedor pede a troca de dono dessa UC (venda do estabelecimento). */
  consumo_medio_kwh?: number | null;
  empresa_parceira_id?: string | null;
}

/** Primeira UC da lista (fora `ignorarId`) que já usa algum dos dois números. */
export const acharUcComMesmoNumero = <T extends UcComNumeros>(
  numeroUc: string | null | undefined,
  outroNumeroUc: string | null | undefined,
  unidades: T[],
  ignorarId?: number | string | null
): T | null => {
  const alvo = new Set(ucCodigoKeys(numeroUc, outroNumeroUc));
  if (alvo.size === 0) return null;
  return (
    unidades.find(
      u =>
        (ignorarId == null || String(u.id) !== String(ignorarId)) &&
        ucCodigoKeys(u.numero_uc, u.outro_numero_uc).some(k => alvo.has(k))
    ) ?? null
  );
};

/**
 * Número que aparece em mais de uma UC do mesmo lote (wizard do CRM), já
 * normalizado. `null` se não há repetição. Uma UC com o mesmo número nas duas
 * colunas não conta como repetição.
 */
export const numeroRepetidoNoLote = (
  ucs: { numero_uc?: string | null; outro_numero_uc?: string | null }[]
): string | null => {
  const vistos = new Set<string>();
  for (const uc of ucs) {
    const chaves = ucCodigoKeys(uc.numero_uc, uc.outro_numero_uc);
    const repetida = chaves.find(k => vistos.has(k));
    if (repetida) return repetida;
    chaves.forEach(k => vistos.add(k));
  }
  return null;
};

/** Texto do aviso quando a tela acha a UC antes de gravar. */
export const descreverUcExistente = (uc: UcExistente): string => {
  const numeros = [uc.numero_uc, uc.outro_numero_uc].filter(Boolean).join(' / ');
  const cliente = uc.nome_cliente?.trim() ? ` de ${uc.nome_cliente.trim()}` : '';
  return `Essa UC já está cadastrada${cliente} (UC ${numeros}). Use o cadastro existente em vez de criar outro.`;
};

/**
 * Mensagem do erro da trigger (23505 com DETAIL `uc_duplicada:<id>:<numero>`).
 * `null` para qualquer outro erro — inclusive o 23505 do UNIQUE(numero_uc).
 */
export const mensagemUcDuplicada = (
  error: { code?: string; details?: string | null; message?: string } | null | undefined
): string | null => {
  if (!error || error.code !== '23505') return null;
  if (typeof error.details !== 'string' || !error.details.startsWith('uc_duplicada:')) return null;
  return error.message || 'Essa UC já está cadastrada no sistema.';
};

/**
 * Procura no banco uma UC que já use algum dos números (nas duas colunas,
 * normalizado). A RLS limita o que cada usuário enxerga; o que ele não enxerga a
 * trigger barra na gravação.
 */
export async function buscarUcComMesmoNumero(
  client: Pick<SupabaseClient, 'from'>,
  numeroUc: string | null | undefined,
  outroNumeroUc: string | null | undefined,
  ignorarId?: number | string | null
): Promise<UcExistente | null> {
  const chaves = ucCodigoKeys(numeroUc, outroNumeroUc);
  if (chaves.length === 0) return null;
  // Aspas: o número normalizado ainda pode ter '/', ',' ou letras digitadas.
  const lista = chaves.map(k => `"${k.replace(/"/g, '')}"`).join(',');
  const { data, error } = await client
    .from('unidades_consumidoras')
    .select('id, numero_uc, outro_numero_uc, nome_cliente, negociacao_id, consumo_medio_kwh, empresa_parceira_id')
    .or(`numero_uc_norm.in.(${lista}),outro_numero_uc_norm.in.(${lista})`)
    .limit(5);
  if (error) throw error;
  return acharUcComMesmoNumero(numeroUc, outroNumeroUc, (data ?? []) as UcExistente[], ignorarId);
}
